import i18n from '../i18n';

const DOCUMENT_TYPES = {
  transcript: 'Transcript',
  patient_note: 'Patient Note',
  treatment_plan_letter: 'Treatment Plan Letter',
  consultation_letter: 'Consultation Letter',
  referral_letter: 'Referral Letter',
  soap_note: 'SOAP Note',
  discharge_summary: 'Discharge Summary',
  specialty_template: 'Specialty Template',
  prescription: 'Prescription',
  diagnosis: 'Diagnosis',
  other: 'Document',
};
const legacyKind = value => Object.keys(DOCUMENT_TYPES).find(kind =>
  DOCUMENT_TYPES[kind].toLowerCase() === String(value || '').trim().toLowerCase());
const knownKind = value => typeof value === 'string' && Object.prototype.hasOwnProperty.call(DOCUMENT_TYPES, value);

// Use the API identifier whenever available. English fallbacks support older
// stored responses; translated labels are never workflow identifiers.
export const getDocumentKind = document => {
  if (document && typeof document === 'object') {
    if (typeof document.document_kind === 'string' && document.document_kind) {
      return knownKind(document.document_kind) ? document.document_kind : 'other';
    }
    if (typeof document.note_type === 'string' && document.note_type) {
      return legacyKind(document.note_type) || 'other';
    }
    if (typeof document.title === 'string' && document.title.trim()) {
      return legacyKind(document.title) || 'other';
    }
  }
  const text = typeof document === 'string' ? document : document?.note;
  if (knownKind(text)) return text;
  if (legacyKind(text)) return legacyKind(text);
  const heading = typeof text === 'string' ? text.trim().split(/\r?\n/, 1)[0] : '';
  // Preserve the display fallback for old unstructured patient notes. Receipt
  // detection intentionally uses the stricter getAutomaticNoteType helper.
  return (heading.startsWith('# ') && legacyKind(heading.slice(2))) || 'patient_note';
};

// Retain canonical English return values for existing comparisons. Render with
// getDocumentLabel; callers with a note record should pass the complete object.
export const getDocumentType = document => DOCUMENT_TYPES[getDocumentKind(document)];

export const getDocumentLabel = document => {
  const kind = typeof document === 'string' && legacyKind(document)
    ? legacyKind(document) : getDocumentKind(document);
  if (kind === 'other' && document?.title) return document.title;
  return i18n.t(`documents.${kind}`, { defaultValue: DOCUMENT_TYPES[kind] });
};

export const formatTranscriptForDisplay = (text, document) => {
  if (typeof text !== 'string') return '';
  const trimmed = text.trim();
  // The API's stable kind selects formatting. The heading can be in any
  // language; historical English records use the existing legacy classifier.
  if (getDocumentKind(document || text) !== 'transcript') return text;
  const match = trimmed.match(/^(# [^\r\n]+?)[ \t]*(?:\r?\n|$)/);
  if (!match) return text;
  const formattedBody = trimmed.slice(match[0].length).trim()
    .replace(/\n{3,}/g, '\n\n')
    .replace(/([.?!])\s+/g, '$1\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return `${match[1]}\n\n${formattedBody}`;
};

export const addDocumentNumbering = notes => {
  const groupKey = note => {
    const kind = getDocumentKind(note);
    return kind === 'other' ? `other:${note.title || ''}` : kind;
  };
  const totals = new Map();
  const counts = new Map();
  notes.forEach(note => totals.set(groupKey(note), (totals.get(groupKey(note)) || 0) + 1));
  return notes.map(note => {
    const key = groupKey(note);
    const number = (counts.get(key) || 0) + 1;
    counts.set(key, number);
    const label = getDocumentLabel(note);
    return { ...note, type: totals.get(key) > 1 ? `${label} ${number}` : label };
  });
};

const safeFilenamePart = value => String(value || '').replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, '_');
const downloadDocument = (note, filename) => {
  // Labels and filenames follow the UI; saved clinical content is unchanged.
  const blob = new Blob([note.note || ''], { type: 'text/plain;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
};

const documentFilename = (note, formatTimestampForUser, sessionSuffix = false) => {
  const label = safeFilenamePart(getDocumentLabel(note));
  const timestamp = safeFilenamePart(formatTimestampForUser(note.created_at, {
    year: 'numeric', month: '2-digit', day: '2-digit',
  }));
  const session = sessionSuffix && note.session_id ? `_${safeFilenamePart(note.session_id.slice(0, 8))}` : '';
  return `${label}_${timestamp}${session}.txt`;
};

export const exportDocument = (note, formatTimestampForUser) => {
  downloadDocument(note, documentFilename(note, formatTimestampForUser));
};

export const exportAllSessionDocuments = (sessionData, formatTimestampForUser) => {
  sessionData.notes.forEach(note => downloadDocument(note, documentFilename(note, formatTimestampForUser, true)));
};
